<template>
  <div>
    <CheckoutModal
      v-if="showCheckoutOverviewModal && order?.id"
      :orderId="order.id"
      :invoiceJustIssued="invoiceJustIssued"
    />

    <SelectPosTerminalModal
      v-if="showSelectTerminalModal"
      :amount="amount"
      :terminals="posTerminals"
      :selectedTerminal="selectedPosTerminal || ''"
      @close="$emit('close')"
      @submit="
        (terminalUid) => {
          showSelectTerminalModal = false;
          selectedPosTerminal = terminalUid;
          showPaymentSteps = true;
        }
      "
    />
    <TransactionFlow
      v-if="showPaymentSteps"
      v-bind="{ status, amount, refusalReason, tipAmount }"
      @retry="retryPayment"
      @close="handleTransactionFlowClose"
    />
  </div>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery, useSubscription } from '@vue/apollo-composable';
import { useStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import CheckoutModal from '@/modules/register/builder/checkout-modal/index.vue';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import type { PosPaymentRefusalReason, PosTerminal } from '@/types';
import { logPaymentEvent } from '@/utils/datadog-logger';
import { mutateOrder, setItems, setTransactions } from '../../../actions/order';
import {
  GENERATE_POS_PAYMENT,
  GET_ORDER,
  LISTEN_TO_POS_TERMINAL_PAYMENT,
  VERIFY_POS_PAYMENT,
} from '../graphql';
import SelectPosTerminalModal from './SelectPosTerminal.vue';
import TransactionFlow from './TransactionFlow.vue';

const props = defineProps<{
  amount: number;
  posTerminals: PosTerminal[];
}>();

const { order } = useRegisterOrderStore();

const invoiceJustIssued = ref(false);

const locationTerminals = computed(() =>
  props.posTerminals.filter(
    (terminal) => terminal.location?.id === order.locationId,
  ),
);

const showSelectTerminalModal = ref(locationTerminals.value.length > 1);
const showPaymentSteps = ref(!showSelectTerminalModal.value);
const showCheckoutOverviewModal = ref(false);

const selectedPosTerminal = useStorage(
  'selected_pos_terminal',
  locationTerminals.value.length
    ? locationTerminals.value[0].terminalUid
    : null,
);
if (
  !locationTerminals.value.find(
    (terminal) => terminal.terminalUid === selectedPosTerminal.value,
  )
) {
  // If theres only one location terminal, set it as selected
  // If the selected terminal which has been stored in localstorage does not exist in the list of terminals, reset it
  // This can happen after switching locations
  if (locationTerminals.value.length === 1) {
    selectedPosTerminal.value = locationTerminals.value[0].terminalUid;
  } else {
    selectedPosTerminal.value = null;
  }
}

const { outstandingAmount, selectedCustomer } = storeToRefs(
  useRegisterOrderStore(),
);
const status = ref('default');
const paymentInTerminal = ref(false);
const refusalReason = ref<PosPaymentRefusalReason | null>(null);
const tipAmount = ref<number>(0);

const emit = defineEmits(['close']);

const { call: callOrderMutation, onDone: onDoneMutation } = mutateOrder();

const { mutate: generatePosPayment } = useMutation(GENERATE_POS_PAYMENT);
const { mutate: verifyPosPayment } = useMutation(VERIFY_POS_PAYMENT);

const subscriptionStartTime = ref<number | null>(null);

const { onResult: handlePayment, onError: handlePaymentError } =
  useSubscription(LISTEN_TO_POS_TERMINAL_PAYMENT);

logPaymentEvent('pos_payment_subscription_started', {
  orderId: order.id,
  timestamp: new Date().toISOString(),
});
subscriptionStartTime.value = Date.now();

handlePayment(
  ({
    data: {
      posTerminalPayment: {
        status: broadcastedStatus,
        order: broadcastedOrder,
        refusalReason: broadcastedRefusalReason,
        tipAmount: broadcastedTipAmount,
      },
    },
  }) => {
    const elapsedTime = subscriptionStartTime.value
      ? Date.now() - subscriptionStartTime.value
      : null;

    logPaymentEvent('pos_terminal_payment_received', {
      orderId: String(broadcastedOrder.id),
      status: broadcastedStatus,
      elapsedMs: elapsedTime,
      expectedTimeout: verificationTimeout ? 'pending' : 'cleared',
    });

    if (String(order.id) === String(broadcastedOrder.id)) {
      paymentInTerminal.value = false;

      // Clear verification timeout if webhook response received
      if (verificationTimeout) {
        clearTimeout(verificationTimeout);
        verificationTimeout = null;
        logPaymentEvent('pos_verification_timeout_cleared', {
          orderId: String(order.id),
          reason: 'subscription_update_received',
          elapsedMs: elapsedTime,
        });
      }

      setExistingOrderData(broadcastedOrder);

      refusalReason.value = broadcastedRefusalReason;
      tipAmount.value = broadcastedTipAmount;

      const orderStore = useRegisterOrderStore();
      orderStore.tipAmount = broadcastedTipAmount;

      switch (broadcastedStatus) {
        case 'paid':
          handlePaidTransaction();
          break;
        case 'failed':
        case 'expired':
        case 'cancelled':
          handleFailedTransaction();
          break;
      }
    }
  },
);

handlePaymentError((error: any) => {
  logPaymentEvent('pos_payment_subscription_error', {
    orderId: order.id,
    error: error.message,
    networkError: error.networkError?.message,
  });
});

let verificationTimeout: any = null;
let currentPosPaymentId: string | null = null;
let currentTerminalUid: string | null = null;

const setExistingOrderData = (broadcastedOrder: any) => {
  order.invoicedAt = broadcastedOrder.invoicedAt;
  order.pending = broadcastedOrder.pending;
  order.fiscalizationFinished = broadcastedOrder.fiscalizationFinished;
  order.number = broadcastedOrder.number;
  selectedCustomer.value = broadcastedOrder.customer;
  // reset items & transactions to prevent duplicates
  order.items = [];
  order.transactions = [];
  setTransactions(broadcastedOrder.transactions);
  setItems(broadcastedOrder.items, { action: 'update' });
};

const handlePaidTransaction = () => {
  if (outstandingAmount.value > 0) {
    status.value = 'success';
    setTimeout(() => {
      emit('close');
      status.value = 'default';
    }, 4000);
  } else {
    completeOrder();
  }
};

const startVerificationTimeout = () => {
  const timeoutMs = 90000; // 90 second timeout for initial verification
  const startTime = Date.now();

  logPaymentEvent('pos_verification_timeout_started', {
    orderId: order.id,
    timeoutMs,
  });

  verificationTimeout = setTimeout(() => {
    const elapsed = Date.now() - startTime;

    logPaymentEvent('pos_verification_timeout_fired', {
      orderId: order.id,
      elapsedMs: elapsed,
      status: status.value,
      paymentInTerminal: paymentInTerminal.value,
    });

    if (paymentInTerminal.value && currentPosPaymentId && currentTerminalUid) {
      verifyPosPayment({
        input: {
          posPaymentId: currentPosPaymentId,
          terminalUid: currentTerminalUid,
        },
      }).then((response) => {
        if (!paymentInTerminal.value) {
          return;
        }

        if (!response?.data?.verifyPosPayment) {
          return;
        }

        const {
          error,
          posPayment,
          order: verifiedOrder,
        } = response.data.verifyPosPayment;

        if (error) {
          handleFailedTransaction();
          return;
        }

        if (posPayment?.status === 'paid') {
          if (getOrderTimeout) {
            clearTimeout(getOrderTimeout);
            getOrderTimeout = null;
          }
          if (verifiedOrder) {
            onGetOrderResult(verifiedOrder);
          } else {
            getOrder();
          }
        } else if (
          posPayment?.status !== 'processing' &&
          posPayment?.status !== 'pending'
        ) {
          handleFailedTransaction();
        } else {
          // Payment is still being processed by the terminal. Adyen's
          // terminal API times out after 150s, so a definitive answer
          // (paid or failed) will always arrive via the subscription or
          // the getOrderTimeout last resort before then. Keep
          // currentPosPaymentId/currentTerminalUid so the subscription
          // can still resolve this payment.
          status.value = 'waiting';
        }
      });
    }
  }, timeoutMs);
};

const startTransaction = () => {
  invoiceJustIssued.value = !order.hasInvoice;
  if (order.id) {
    callOrderMutation('update');
  } else {
    order.pending = true;
    callOrderMutation('create');
  }
};

watch(
  showPaymentSteps,
  (value) => {
    if (value) {
      startTransaction();
    }
  },
  {
    immediate: true,
  },
);

onDoneMutation(({ data }) => {
  const orderResponse = data.createOrder
    ? data.createOrder.order
    : data.updateOrder.order;

  if (orderResponse) {
    order.id = orderResponse.id;
    sendPaymentToTerminal();
  } else {
    handleFailedTransaction();
  }
});

const sendPaymentToTerminal = () => {
  paymentInTerminal.value = true;

  generatePosPayment({
    input: {
      orderId: order.id,
      terminalUid: selectedPosTerminal.value,
      amount: props.amount,
      outstandingAmount: outstandingAmount.value,
    },
  }).then((response) => {
    if (!response?.data?.generatePosPayment) {
      handleFailedTransaction();
      return;
    }

    const { error, posPaymentId } = response.data.generatePosPayment;

    if (error) {
      handleFailedTransaction();
    } else {
      status.value = 'pending';
      currentPosPaymentId = posPaymentId;
      currentTerminalUid = selectedPosTerminal.value;

      // Wait 30 seconds to synchronously verify the payment if no webhook response is received
      startVerificationTimeout();
    }
  });
};

const completeOrder = () => {
  status.value = 'success';

  setTimeout(() => {
    showPaymentSteps.value = false;
  }, 4000);

  setTimeout(() => {
    showCheckoutOverviewModal.value = true;
  }, 4100);
};

const handleFailedTransaction = () => {
  status.value = 'failed';

  // Clear verification timeout on failure
  if (verificationTimeout) {
    clearTimeout(verificationTimeout);
    verificationTimeout = null;
  }
  currentPosPaymentId = null;
  currentTerminalUid = null;
};

const retryPayment = () => {
  status.value = 'retry';

  // Clear any existing verification timeout before retrying
  if (verificationTimeout) {
    clearTimeout(verificationTimeout);
    verificationTimeout = null;
  }
  currentPosPaymentId = null;
  currentTerminalUid = null;

  sendPaymentToTerminal();
};

let getOrderTimeout: any = null;

// Adyen's terminal payment times out after 2m30s (150s), so by then a
// definitive answer has always arrived. This is the actual last resort:
// if the order is still unpaid at this point, treat it as failed.
const GET_ORDER_TIMEOUT_MS = 150000;

watch(
  () => paymentInTerminal.value,
  (value) => {
    if (value) {
      getOrderTimeout = setTimeout(() => {
        getOrder();
      }, GET_ORDER_TIMEOUT_MS);
    } else {
      if (getOrderTimeout) {
        clearTimeout(getOrderTimeout);
        getOrderTimeout = null;
      }

      if (verificationTimeout) {
        clearTimeout(verificationTimeout);
        verificationTimeout = null;
      }
    }
  },
);

const getOrder = () => {
  if (order.id) {
    const { onResult } = useQuery(
      GET_ORDER,
      { id: order.id },
      { fetchPolicy: 'network-only' },
    );

    onResult(({ data: { order: orderResponse } }) => {
      if (orderResponse) {
        onGetOrderResult(orderResponse);
      }
    });
  }
};

const onGetOrderResult = (orderResponse: any) => {
  paymentInTerminal.value = false;
  const transactionAdded =
    orderResponse.transactions.length > order.transactions.length;

  setExistingOrderData(orderResponse);

  if (transactionAdded) {
    handlePaidTransaction();
  } else {
    handleFailedTransaction();
  }
};

const handleTransactionFlowClose = () => {
  if (status.value === 'success' && outstandingAmount.value <= 0) {
    showPaymentSteps.value = false;
    showCheckoutOverviewModal.value = true;
  } else {
    emit('close');
  }
};
</script>
